<script>
import * as m from "@app/paraglide/messages.js";
import { editorState, saveEditorSettings } from "@app/store/editorStore.svelte.js";
import { zoomIn, zoomOut } from "@app/lib/timeline/viewport.svelte.js";
import {
    timelineTracksState,
    addTrack as addTimelineTrack
} from "@app/store/timelineTracksStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";

/**
 * Floating toolbar for the Konva timeline. Mirrors the controls offered by
 * the legacy WaveSurfer timeline plus the additions called for in the spec.
 *
 * @typedef {Object} Props
 * @property {boolean} showMarkers
 * @property {() => void} onToggleMarkers
 * @property {() => void} onSplit
 * @property {() => void} onMerge
 * @property {number} [selectedCount]   - number of selected events; affects merge button enable state
 */

/** @type {Props} */
let {
    showMarkers = true,
    onToggleMarkers,
    onSplit,
    onMerge,
    selectedCount = 0
} = $props();

let snapOn = $derived(Boolean(editorState.snapToShotChanges || editorState.snapToRegions));
let drawOn = $derived(Boolean(editorState.drawEvents));
let allowOverlapOn = $derived(editorState.timelineOverlapProtection === false);

function toggleSnap() {
    const next = !snapOn;
    editorState.snapToShotChanges = next;
    editorState.snapToRegions = next;
    saveEditorSettings();
}

function toggleDraw() {
    editorState.drawEvents = !editorState.drawEvents;
    saveEditorSettings();
}

function toggleAllowOverlap() {
    // Flip protection; `allowOverlapOn` reflects the current (pre-toggle) state,
    // so the new protection value is exactly `allowOverlapOn`.
    editorState.timelineOverlapProtection = allowOverlapOn;
    saveEditorSettings();
}

function handleZoomIn(e) {
    zoomIn();
    e?.currentTarget?.blur?.();
}

function handleZoomOut(e) {
    zoomOut();
    e?.currentTarget?.blur?.();
}

function handleSplit(e) {
    onSplit?.();
    e?.currentTarget?.blur?.();
}

function handleMerge(e) {
    onMerge?.();
    e?.currentTarget?.blur?.();
}

function handleToggleMarkers(e) {
    onToggleMarkers?.();
    e?.currentTarget?.blur?.();
}

let addOpen = $state(false);
let addBtnEl = $state(null);
let menuEl = $state(null);

let availableGroups = $derived.by(() => {
    const out = [];
    for (let i = 0; i < eventGroupState.length; i += 1) {
        if (i === projectState.selected) continue;
        if (timelineTracksState.additional.includes(i)) continue;
        out.push({ index: i, name: eventGroupState[i]?.name || `Track ${i + 1}` });
    }
    return out;
});

function toggleAddMenu(e) {
    addOpen = !addOpen;
    e?.stopPropagation?.();
}

function handlePickGroup(index) {
    addTimelineTrack(index, projectState.selected);
    addOpen = false;
}

function handleDocClick(ev) {
    if (!addOpen) return;
    if (menuEl?.contains(ev.target)) return;
    if (addBtnEl?.contains(ev.target)) return;
    addOpen = false;
}
</script>

<div
    class="timeline-toolbar btn-group btn-group-sm shadow"
    role="group"
    aria-label={m.timeline_controls_aria_label()}>
    <button
        type="button"
        class="btn btn-light"
        aria-label={m.timeline_split_event_aria_label()}
        title={m.timeline_split_event_title()}
        onclick={handleSplit}>
        <i class="bi bi-vr"></i>
    </button>
    <button
        type="button"
        class="btn btn-light"
        aria-label={m.timeline_merge_event_aria_label()}
        title={m.timeline_merge_event_title()}
        disabled={selectedCount < 2}
        onclick={handleMerge}>
        <i class="bi bi-ticket-perforated-fill"></i>
    </button>

    <button
        type="button"
        class="btn btn-secondary"
        aria-label={m.timeline_zoom_out_aria_label()}
        title={m.timeline_zoom_out_title()}
        onclick={handleZoomOut}>
        <i class="bi bi-zoom-out"></i>
    </button>
    <button
        type="button"
        class="btn btn-secondary"
        aria-label={m.timeline_zoom_in_aria_label()}
        title={m.timeline_zoom_in_title()}
        onclick={handleZoomIn}>
        <i class="bi bi-zoom-in"></i>
    </button>

    <button
        type="button"
        class="btn btn-{snapOn ? 'primary' : 'secondary'}"
        aria-label="Toggle snapping"
        aria-pressed={snapOn}
        title="Snap to markers and event edges"
        onclick={toggleSnap}>
        <i class="bi bi-magnet{snapOn ? '-fill' : ''}"></i>
    </button>

    <button
        type="button"
        class="btn btn-{drawOn ? 'primary' : 'secondary'}"
        aria-label="Toggle draw events"
        aria-pressed={drawOn}
        title="Click-and-drag empty timeline to create events"
        onclick={toggleDraw}>
        <i class="bi bi-pencil{drawOn ? '-fill' : ''}"></i>
    </button>

    <button
        type="button"
        class="btn btn-{allowOverlapOn ? 'primary' : 'secondary'}"
        aria-label={m.timeline_allow_overlap_aria_label()}
        aria-pressed={allowOverlapOn}
        title={m.timeline_allow_overlap_title()}
        onclick={toggleAllowOverlap}>
        <i class="bi bi-layers{allowOverlapOn ? '-fill' : ''}"></i>
    </button>

    <button
        type="button"
        class="btn btn-{showMarkers ? 'primary' : 'secondary'}"
        aria-label={m.timeline_toggle_markers_aria_label()}
        aria-pressed={showMarkers}
        title={m.timeline_toggle_markers_title()}
        onclick={handleToggleMarkers}>
        <i class="bi bi-geo-alt{showMarkers ? '-fill' : ''}"></i>
    </button>

    <button
        type="button"
        class="btn btn-secondary"
        aria-label="Add track"
        aria-haspopup="menu"
        aria-expanded={addOpen}
        title="Add another event group as a timeline track"
        bind:this={addBtnEl}
        disabled={availableGroups.length === 0}
        onclick={toggleAddMenu}>
        <i class="bi bi-plus-square"></i>
    </button>
</div>

{#if addOpen && availableGroups.length > 0}
    <div class="add-menu shadow" role="menu" bind:this={menuEl}>
        <div class="add-menu-title">Add to timeline</div>
        {#each availableGroups as g (g.index)}
            <button
                type="button"
                class="add-menu-item"
                role="menuitem"
                onclick={() => handlePickGroup(g.index)}>
                <i class="bi bi-plus-circle" aria-hidden="true"></i>
                <span>{g.name}</span>
            </button>
        {/each}
    </div>
{/if}

<svelte:window onclick={handleDocClick} />

<style>
.timeline-toolbar {
    bottom: 10px;
    position: absolute;
    right: 10px;
    z-index: 20;
}

.add-menu {
    background: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 6px;
    bottom: 48px;
    color: var(--bs-body-color);
    max-height: 240px;
    min-width: 200px;
    overflow-y: auto;
    padding: 4px 0;
    position: absolute;
    right: 10px;
    z-index: 25;
}

.add-menu-title {
    color: var(--bs-secondary-color);
    font-size: 0.72rem;
    padding: 4px 12px;
    text-transform: uppercase;
}

.add-menu-item {
    align-items: center;
    background: transparent;
    border: none;
    color: var(--bs-body-color);
    cursor: pointer;
    display: flex;
    font-size: 0.85rem;
    gap: 8px;
    padding: 6px 12px;
    text-align: left;
    width: 100%;
}

.add-menu-item:hover {
    background: var(--bs-primary-bg-subtle, rgba(13, 110, 253, 0.15));
}

.add-menu-item i {
    color: var(--bs-secondary-color);
}
</style>
